/** Evidence intake before the parent entity exists. */
import { type ReactElement, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { api } from "@/api/client";
import { unwrap } from "@/api/problem";
import { type LinkableEntity, type Uploader, useUploader } from "@/features/documents/queries";
import { Button } from "@/shared/ui/button";

/** Reusable staged evidence contract; link only after the parent has been saved. */
export interface StagedUploader extends Uploader {
  hasFiles: boolean;
  ready: boolean;
  linkTo: (entityType: LinkableEntity, entityId: string) => Promise<void>;
  cancelAll: () => Promise<void>;
}

/** Upload unlinked drafts and publish them idempotently once the parent exists. */
export function useStagedAttachments(): StagedUploader {
  const { t } = useTranslation();
  const uploader = useUploader({ staged: true });
  return {
    ...uploader,
    hasFiles: uploader.uploads.length > 0,
    ready: uploader.uploads.every((u) => u.status === "ready"),
    linkTo: async (entityType, entityId) => {
      if (uploader.uploads.some((u) => u.status !== "ready")) {
        throw new Error(t("documents.notReady"));
      }
      for (const row of uploader.uploads) {
        if (!row.documentId) continue;
        unwrap(
          await api.POST("/api/v1/documents/{document_id}/links", {
            params: { path: { document_id: row.documentId } },
            body: { entity_type: entityType, entity_id: entityId, relation: "evidence" },
          }),
        );
      }
    },
    cancelAll: async () => {
      for (const row of uploader.uploads) await uploader.cancel(row.id);
    },
  };
}

/** Pending, transferring, failed and ready uploads with safe retry/cancel controls. */
export function UploadQueue({ uploader }: { uploader: Uploader }): ReactElement {
  const { t } = useTranslation();
  const [cancelError, setCancelError] = useState("");
  return (
    <>
      {cancelError ? <p role="alert">{cancelError}</p> : null}
      <ul className="space-y-1" aria-label={t("documents.uploads")} aria-live="polite">
        {uploader.uploads.map((row) => (
          <li key={row.id} className="flex flex-wrap items-center gap-2 text-xs">
            <span className="min-w-0 flex-1 break-words">{row.file.name}</span>
            <span aria-label={t(`documents.uploadState.${row.status}`)}>
              {row.status === "ready"
                ? t("documents.uploaded")
                : t(`documents.uploadState.${row.status}`)}
            </span>
            {row.status === "uploading" ? (
              <progress max={100} value={row.progress} aria-label={row.file.name} />
            ) : null}
            {row.status === "failed" ? (
              <>
                <span role="alert" className="text-destructive">
                  {t("documents.uploadFailed")} {row.error}
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => void uploader.retry(row.id)}
                >
                  {t("documents.retryUpload")}
                </Button>
              </>
            ) : null}
            {row.status !== "ready" || "cancelAll" in uploader ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setCancelError("");
                  void uploader
                    .cancel(row.id)
                    .catch((error: unknown) =>
                      setCancelError(error instanceof Error ? error.message : String(error)),
                    );
                }}
              >
                {t("app.cancel")}
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </>
  );
}

/** Picker/camera intake usable in any create form before an entity ID exists. */
export function StagedAttachments({
  uploader,
  camera = true,
}: {
  uploader: StagedUploader;
  camera?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const picker = useRef<HTMLInputElement>(null);
  const capture = useRef<HTMLInputElement>(null);
  return (
    <section className="space-y-2" aria-label={t("documents.uploads")}>
      <p className="text-xs text-muted-foreground">{t("documents.stagedHint")}</p>
      <input
        ref={picker}
        type="file"
        multiple
        accept="image/*,.heic,.heif"
        className="hidden"
        aria-label={t("documents.chooseFiles")}
        onChange={(e) => {
          if (e.target.files) void uploader.upload(e.target.files);
          e.target.value = "";
        }}
      />
      <input
        ref={capture}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        aria-label={t("documents.takePhoto")}
        onChange={(e) => {
          if (e.target.files) void uploader.upload(e.target.files);
          e.target.value = "";
        }}
      />
      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={() => picker.current?.click()}>
          {t("documents.chooseFiles")}
        </Button>
        {camera ? (
          <Button type="button" variant="outline" onClick={() => capture.current?.click()}>
            {t("documents.takePhoto")}
          </Button>
        ) : null}
      </div>
      <UploadQueue uploader={uploader} />
    </section>
  );
}
